/**
 * Schedule-edit deep-link footer for scheduled-task deliveries (ENG-4462).
 *
 * ENG-4444 added the `/agents/:agentId/schedules/:taskId` route and a
 * `build_schedule_edit_link` MCP tool; ENG-4456 added a prompt-preamble line
 * telling the agent to embed kanban deep links. Neither guarantees the user
 * sees a clickable edit link on *every* scheduled delivery — the agent has
 * to opt in. This module closes that gap delivery-side by appending a
 * schedule-edit link to the body before it hits Slack/Telegram.
 *
 * All functions are pure so behaviour is unit-testable without spawning a
 * delivery. The manager-worker wrapper calls into these from
 * deliverScheduledTaskOutput.
 */

/** Media whose footer format differs. Fallback is a plain URL. */
export type FooterMedium = 'slack' | 'telegram' | 'plain';

/** Normalise an agent code_name to the env-var suffix form. Kebab → snake,
 *  uppercased. Mirrors the pattern in delivery-hint.ts for consistency. */
function envSuffixFor(codeName: string): string {
  return codeName.replace(/[^A-Za-z0-9]+/g, '_').toUpperCase();
}

/**
 * Is the schedule-link footer enabled for this delivery?
 *
 * Precedence (matches delivery-hint.ts):
 *   per-agent disable → host disable → default (enabled).
 *
 * Per-agent env: `AGT_SCHEDULE_LINK_FOOTER_DISABLED__<CODE_NAME>=1`
 * Host-global env: `AGT_SCHEDULE_LINK_FOOTER_DISABLED=1`
 */
export function scheduleLinkFooterEnabled(
  codeName?: string,
  env: NodeJS.ProcessEnv = process.env,
): boolean {
  if (codeName) {
    const perAgent = env[`AGT_SCHEDULE_LINK_FOOTER_DISABLED__${envSuffixFor(codeName)}`];
    if (perAgent === '1') return false;
  }
  if (env['AGT_SCHEDULE_LINK_FOOTER_DISABLED'] === '1') return false;
  return true;
}

/**
 * Resolve the webapp console URL from env. Manager-worker runs client-side,
 * so we can't derive from a request Origin — we rely on the operator
 * configuring `AGT_CONSOLE_URL` (the canonical name per the SST setup) or
 * `NEXT_PUBLIC_APP_URL` (local-dev parity with the rest of the stack).
 * Returns null if neither is set; callers should skip footer emission.
 */
export function getConsoleUrl(env: NodeJS.ProcessEnv = process.env): string | null {
  // Evaluate each candidate trimmed and fall back past whitespace-only
  // values. Plain `AGT_CONSOLE_URL || NEXT_PUBLIC_APP_URL` would treat a
  // whitespace-only AGT_CONSOLE_URL as truthy and suppress the fallback,
  // returning null despite a valid NEXT_PUBLIC_APP_URL being set.
  const canonical = env['AGT_CONSOLE_URL']?.trim();
  if (canonical) return canonical.replace(/\/+$/, '');

  const fallback = env['NEXT_PUBLIC_APP_URL']?.trim();
  if (fallback) return fallback.replace(/\/+$/, '');

  return null;
}

/**
 * Build the canonical schedule-edit URL. Mirrors
 * packages/openclaw-plugin-augmented/src/tools/schedule-link-url.ts so the
 * CLI isn't pulling an openclaw-plugin dep it doesn't otherwise use.
 * agentId / taskId are encodeURIComponent'd defensively.
 */
export function buildScheduleEditLink(
  consoleUrl: string,
  agentId: string,
  taskId: string,
): string {
  const base = consoleUrl.replace(/\/+$/, '');
  return `${base}/agents/${encodeURIComponent(agentId)}/schedules/${encodeURIComponent(taskId)}`;
}

/**
 * Format the footer line for a given medium.
 * - Slack: `<url|Edit schedule>` — Slack's native link syntax, keeps the
 *   URL out of the visible text.
 * - Telegram: `Edit schedule: <url>` — Telegram's text-mode sendMessage
 *   auto-linkifies bare URLs, so a plain label + URL reads cleanly.
 * - plain: just the URL, for callers that aren't sure which medium it is.
 */
export function formatScheduleLinkFooter(url: string, medium: FooterMedium): string {
  if (medium === 'slack') return `<${url}|Edit schedule>`;
  if (medium === 'telegram') return `Edit schedule: ${url}`;
  return url;
}

/**
 * Append the schedule-edit footer to a body. Separated from the body by a
 * blank line, and idempotent against the identical footer — retries /
 * re-deliveries won't stack multiple copies.
 */
export function appendScheduleLinkFooter(
  body: string,
  url: string,
  medium: FooterMedium,
): string {
  const footer = formatScheduleLinkFooter(url, medium);
  const trimmed = body.replace(/\s+$/, '');
  if (trimmed.endsWith(footer)) return trimmed;
  return `${trimmed}\n\n${footer}`;
}

/**
 * ENG-4495: one-time warning guard. We used to silently drop the footer
 * when AGT_CONSOLE_URL was unset, which is how Scout's prod host went
 * months without deep links before anyone noticed. Emit exactly one log
 * line per process so misconfigured hosts leave a breadcrumb in
 * ~/.augmented/manager.log without spamming it on every delivery tick.
 */
let warnedNullConsoleUrl = false;

/** Test helper — reset the once-per-process warning latch between tests. */
export function resetConsoleUrlWarning(): void {
  warnedNullConsoleUrl = false;
}

/**
 * Convenience wrapper — the thing the manager-worker will call on each
 * successful delivery. Returns the body unchanged if any precondition fails
 * (disabled, missing console URL, missing taskId, etc.) so callers don't
 * need a pile of guards at every integration site.
 *
 * `log` is optional so tests / tooling can call this without a logger;
 * manager-worker passes its persistent `log()` so the missing-console-URL
 * warning lands in manager.log alongside other delivery diagnostics.
 */
export function withScheduleLinkFooter(opts: {
  body: string;
  medium: FooterMedium;
  codeName?: string;
  agentId: string;
  taskId?: string;
  env?: NodeJS.ProcessEnv;
  log?: (msg: string) => void;
}): string {
  if (!opts.taskId) return opts.body;
  if (!scheduleLinkFooterEnabled(opts.codeName, opts.env)) return opts.body;
  const consoleUrl = getConsoleUrl(opts.env);
  if (!consoleUrl) {
    if (!warnedNullConsoleUrl && opts.log) {
      warnedNullConsoleUrl = true;
      try {
        opts.log(
          '[schedule-link] AGT_CONSOLE_URL unset and NEXT_PUBLIC_APP_URL unset — schedule-edit deep-link footer disabled. Run `agt setup` again or export AGT_CONSOLE_URL (e.g. https://app.augmented.team) to restore it.',
        );
      } catch {
        // Diagnostic log must never break delivery fallback — swallow.
      }
    }
    return opts.body;
  }
  const url = buildScheduleEditLink(consoleUrl, opts.agentId, opts.taskId);
  return appendScheduleLinkFooter(opts.body, url, opts.medium);
}
